import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const GalleryPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const images = [
    {
      id: 1,
      src: "/gallery-1.jpeg",
      alt: "Personal training sessie",
      caption: "Personal Training",
    },
    {
      id: 2,
      src: "/gallery-2.jpeg",
      alt: "Coach Jan met klant",
      caption: "Coaching & Begeleiding",
    },
    {
      id: 3,
      src: "/gallery-3.jpeg",
      alt: "Training op machines",
      caption: "Professionele Apparatuur",
    },
    {
      id: 4,
      src: "/gallery-4.jpeg",
      alt: "Bokstraining",
      caption: "Gevarieerde Training",
    },
    {
      id: 5,
      src: "/gallery-5.jpeg",
      alt: "Cardio training",
      caption: "Cardio & Conditie",
    },
    {
      id: 6,
      src: "/gallery-6.jpeg",
      alt: "Squat training",
      caption: "Krachttraining",
    },
    {
      id: 7,
      src: "/gallery-7.jpeg",
      alt: "Groepsles",
      caption: "Groepslessen",
    },
    {
      id: 8,
      src: "/gallery-8.jpeg",
      alt: "Squat begeleiding",
      caption: "Techniek Coaching",
    },
    {
      id: 9,
      src: "/gallery-9.jpeg",
      alt: "Deadlift training",
      caption: "Deadlift Sessie",
    },
    {
      id: 10,
      src: "/gallery-10.png",
      alt: "Bokstraining met coach",
      caption: "Bokstraining",
    },
    {
      id: 11,
      src: "/gallery-11.png",
      alt: "Dumbbell training begeleiding",
      caption: "Personal Training",
    },
    {
      id: 12,
      src: "/gallery-12.png",
      alt: "Schoudertraining met coach",
      caption: "Schouder Training",
    },
    {
      id: 13,
      src: "/gallery-13.jpeg",
      alt: "Barbell training duo",
      caption: "Samen Trainen",
    },
    {
      id: 14,
      src: "/gallery-14.jpeg",
      alt: "Coaching close-up",
      caption: "Focus & Concentratie",
    },
    {
      id: 15,
      src: "/gallery-15.jpeg",
      alt: "Xenia met jonge sporter",
      caption: "Training Voor Iedereen",
    },
    {
      id: 16,
      src: "/gallery-16.jpeg",
      alt: "Roeimachine groepstraining",
      caption: "Groepstraining",
    },
    {
      id: 17,
      src: "/gallery-17.jpeg",
      alt: "Squat duo training",
      caption: "Squat Sessie",
    },
    {
      id: 18,
      src: "/gallery-18.jpeg",
      alt: "Squat coaching close-up",
      caption: "Techniek Begeleiding",
    },
    {
      id: 19,
      src: "/gallery-19.jpeg",
      alt: "Coaching in de gym",
      caption: "Coaching Sessie",
    },
    {
      id: 20,
      src: "/gallery-20.jpeg",
      alt: "Hip thrust begeleiding",
      caption: "Hip Thrust Training",
    },
    {
      id: 21,
      src: "/gallery-21.jpeg",
      alt: "Hip thrust coaching",
      caption: "Persoonlijke Begeleiding",
    },
    {
      id: 22,
      src: "/gallery-22.jpeg",
      alt: "Barbell coaching",
      caption: "Barbell Coaching",
    },
  ];

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrevious = () => {
    const newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSelectedImage(images[newIndex]);
  };

  const goToNext = () => {
    const newIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSelectedImage(images[newIndex]);
  };

  return (
    <main data-testid="gallery-page" className="pt-20">
      {/* Hero Section */}
      <section className="section bg-[#0A0A0A]" data-testid="gallery-hero">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="caption mb-4 block">Galerij</span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 font-['Barlow_Condensed'] uppercase tracking-tighter">
              Sfeerbeelden
              <br />
              <span className="text-red-500">Van Arena Gym</span>
            </h1>
            <p className="text-zinc-400 text-lg">
              Bekijk onze trainingen, faciliteiten en de energie van onze
              community.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section bg-[#121212]" data-testid="gallery-grid">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {images.map((image, index) => (
              <motion.div
                key={image.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (index % 3) * 0.1 }}
                className="group cursor-pointer relative overflow-hidden rounded-sm aspect-square"
                onClick={() => openLightbox(image, index)}
                data-testid={`gallery-image-${image.id}`}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-300 flex items-end">
                  <div className="p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform translate-y-4 group-hover:translate-y-0">
                    <p className="text-white font-bold text-lg font-['Barlow_Condensed'] uppercase">
                      {image.caption}
                    </p>
                  </div>
                </div>
                <div className="absolute inset-0 border-2 border-transparent group-hover:border-red-500/50 transition-all duration-300 rounded-sm" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={closeLightbox}
            data-testid="lightbox"
          >
            {/* Close button */}
            <button
              className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors z-10"
              onClick={closeLightbox}
              data-testid="lightbox-close"
            >
              <X size={32} />
            </button>

            {/* Previous button */}
            <button
              className="absolute left-4 md:left-8 text-white/70 hover:text-white transition-colors z-10 p-2"
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
              data-testid="lightbox-prev"
            >
              <ChevronLeft size={48} />
            </button>

            {/* Image */}
            <motion.div
              key={selectedImage.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="max-w-5xl max-h-[80vh] mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage.src}
                alt={selectedImage.alt}
                className="max-w-full max-h-[80vh] object-contain rounded-sm"
              />
              <div className="text-center mt-4">
                <p className="text-white font-bold text-xl font-['Barlow_Condensed'] uppercase">
                  {selectedImage.caption}
                </p>
                <p className="text-zinc-500 text-sm mt-1">
                  {currentIndex + 1} / {images.length}
                </p>
              </div>
            </motion.div>

            {/* Next button */}
            <button
              className="absolute right-4 md:right-8 text-white/70 hover:text-white transition-colors z-10 p-2"
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              data-testid="lightbox-next"
            >
              <ChevronRight size={48} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};

export default GalleryPage;
